import React, { useState, useRef } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import styles from "../styles/Contact.module.css";
import heroBackground from "../assets/images/image8.png";

const EMAILJS_SERVICE_ID  = "service_wxxbz9f";
const EMAILJS_TEMPLATE_ID = "template_r79e2w9";
const EMAILJS_PUBLIC_KEY  = "-nEr4M0gJEEt1e3Ei";

function useInView(ref, { threshold = 0.1, once = true } = {}) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); if (once) obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
}

const INFO = [
  { icon: Mail,   label: "Email",       value: "calvarywfm2017@gmail.com", href: "mailto:calvarywfm2017@gmail.com" },
  { icon: Phone,  label: "Phone",       value: "+250 783 517 590",         href: "tel:+250783517590" },
  { icon: MapPin, label: "Head Office", value: "Kigali, Rwanda",           href: null },
];

export default function Contact() {
  const navigate = useNavigate();
  const formRef  = useRef(null);
  const bodyRef  = useRef(null);
  const ctaRef   = useRef(null);
  const bodyVisible = useInView(bodyRef, { threshold: 0.05 });
  const ctaVisible  = useInView(ctaRef,  { threshold: 0.15 });

  const [form, setForm]           = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitStatus, setStatus] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    emailjs
      .sendForm(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, formRef.current, { publicKey: EMAILJS_PUBLIC_KEY })
      .then(() => {
        setStatus("success");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        setTimeout(() => setStatus(null), 5000);
      })
      .catch(() => { setStatus("error"); setTimeout(() => setStatus(null), 5000); })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className={styles.contactPage}>

      {/* ── Cinematic Hero ── */}
      <section className={styles.hero} style={{ backgroundImage: `url(${heroBackground})` }}>
        <div className={styles.heroOverlay} />
        <div className={styles.heroContent}>
          <span className={styles.eyebrow}>CWFM · Contact</span>
          <h1 className={styles.heroTitle}>Get in Touch</h1>
          <div className={styles.titleBar} />
          <p className={styles.heroSub}>
            We would love to hear from you. Reach out to Calvary Wide Fellowship Ministries
            and our team will respond as soon as possible.
          </p>
        </div>
        <div className={styles.scrollCue}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* ── Body — info + form (light) ── */}
      <section className={styles.bodyOuter}>
        <div
          ref={bodyRef}
          className={`${styles.bodyInner} ${bodyVisible ? styles.bodyVisible : ""}`}
        >

          {/* Left — contact info */}
          <div className={styles.infoCol}>
            <span className={styles.infoEyebrow}>Contact Information</span>
            <h2 className={styles.infoTitle}>Let's Connect</h2>
            <p className={styles.infoDesc}>
              Have questions about our church, want to join our community, or need
              prayer support? Send us a message and we will be in touch.
            </p>

            <div className={styles.infoCards}>
              {INFO.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className={styles.infoCard}>
                  <div className={styles.infoIcon}><Icon size={20} /></div>
                  <div>
                    <div className={styles.infoLabel}>{label}</div>
                    {href
                      ? <a href={href} className={styles.infoValue}>{value}</a>
                      : <div className={styles.infoValue}>{value}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <form ref={formRef} onSubmit={handleSubmit} className={styles.formCard}>
            <h3 className={styles.formTitle}>Send a Message</h3>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Full Name</label>
                <input type="text" name="name" value={form.name} onChange={handleChange}
                  className={styles.formInput} placeholder="Your name" required />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Email Address</label>
                <input type="email" name="email" value={form.email} onChange={handleChange}
                  className={styles.formInput} placeholder="your@email.com" required />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Phone (optional)</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                  className={styles.formInput} placeholder="+250 783 517 590" />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Subject</label>
                <input type="text" name="subject" value={form.subject} onChange={handleChange}
                  className={styles.formInput} placeholder="What is this about?" required />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Message</label>
              <textarea name="message" value={form.message} onChange={handleChange}
                className={styles.formTextarea} placeholder="Your message here..." rows="5" required />
            </div>

            <button type="submit" disabled={isSubmitting} className={styles.submitBtn}>
              <Send size={16} />
              {isSubmitting ? "Sending…" : "Send Message"}
            </button>

            {submitStatus === "success" && (
              <div className={styles.successMsg}>
                ✓ Message sent! We will get back to you soon.
              </div>
            )}
            {submitStatus === "error" && (
              <div className={styles.errorMsg}>
                ✗ Something went wrong. Please try again or email us directly.
              </div>
            )}
          </form>

        </div>
      </section>

      {/* ── CTA (dark) ── */}
      <section
        ref={ctaRef}
        className={`${styles.ctaSection} ${ctaVisible ? styles.ctaVisible : ""}`}
        style={{ backgroundImage: `url(${heroBackground})` }}
      >
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaContent}>
          <span className={styles.ctaEyebrow}>Join Our Community</span>
          <h2 className={styles.ctaTitle}>Visit Us Next Sunday</h2>
          <div className={styles.ctaTitleBar} />
          <p className={styles.ctaDesc}>
            Find a fellowship location near you or join us online.
            We would love to worship with you.
          </p>
          <div className={styles.ctaButtons}>
            <button className={styles.ctaBtnGold} onClick={() => navigate("/fellowship")}>
              Visit a Location
            </button>
            <button className={styles.ctaBtnOutline} onClick={() => navigate("/")}>
              Back to Home
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
